import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, type Node, type LatestMetric, humanBytes, timeAgo } from '../lib/api'
import { Card, Spinner } from '../components/ui'
import { IconNodes, IconClients, IconSites, IconDatabase, IconFTP, IconCert, IconCron, IconTasks, IconCircleDot } from '../components/icons'
import { UsageBar } from '../components/UsageBar'

interface Dashboard {
  nodes: number
  clients: number
  sites: number
  databases: number
  ftp_users: number
  cron_jobs: number
  certificates: number
  tasks_active: number
}

const STATUS_RU: Record<string, string> = { online: 'онлайн', offline: 'офлайн', registered: 'подключена' }

export function DashboardPage() {
  const { data: d, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get<Dashboard>('/dashboard')).data,
    refetchInterval: 10000,
  })
  const { data: nodes = [] } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => (await api.get<Node[]>('/nodes')).data,
    refetchInterval: 10000,
  })
  const { data: metrics = {} } = useQuery({
    queryKey: ['nodes-metrics-latest'],
    queryFn: async () => (await api.get<Record<string, LatestMetric>>('/nodes-metrics-latest')).data,
    refetchInterval: 5000,
  })

  if (isLoading || !d) return <div className="p-8"><Spinner /></div>

  const tiles = [
    { icon: IconNodes,    label: 'Ноды',            v: d.nodes,        to: '/nodes' },
    { icon: IconClients,  label: 'Клиенты',         v: d.clients,      to: '/clients' },
    { icon: IconSites,    label: 'Сайты',           v: d.sites,        to: '/sites' },
    { icon: IconDatabase, label: 'Базы данных',     v: d.databases,    to: '/databases' },
    { icon: IconFTP,      label: 'FTP-пользователи', v: d.ftp_users,   to: '/ftp' },
    { icon: IconCron,     label: 'Cron-задачи',     v: d.cron_jobs,    to: '/cron' },
    { icon: IconCert,     label: 'Сертификаты',     v: d.certificates, to: '/certificates' },
    { icon: IconTasks,    label: 'Активные задачи', v: d.tasks_active, to: '/tasks', highlight: d.tasks_active > 0 },
  ]

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Обзор</h1>
        <p className="text-[13px] text-neutral-500 mt-1">Сводка по всему хостингу.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {tiles.map(t => {
          const Icon = t.icon
          return (
            <Link key={t.label} to={t.to}
                  className={`block p-4 border rounded-md bg-white transition ${
                    t.highlight ? 'border-blue-300 hover:border-blue-500' : 'border-neutral-200 hover:border-neutral-400'
                  }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500">{t.label}</div>
                <Icon className={t.highlight ? 'text-blue-600' : 'text-neutral-400'} />
              </div>
              <div className="text-[24px] font-semibold leading-none">{t.v}</div>
            </Link>
          )
        })}
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-100">
          <h2 className="text-[15px] font-semibold">Ноды</h2>
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-neutral-50/60 border-b border-neutral-100 text-[11px] uppercase text-neutral-500 tracking-wider">
            <tr>
              <th className="text-left font-medium px-5 py-2">Имя</th>
              <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Хост</th>
              <th className="text-left font-medium px-4 py-2">Загрузка</th>
              <th className="text-left font-medium px-4 py-2">Статус</th>
              <th className="text-left font-medium px-5 py-2">Пинг</th>
            </tr>
          </thead>
          <tbody>
            {nodes.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-neutral-400">
                Нет нод. <Link to="/nodes" className="text-neutral-900 underline">Добавить</Link>
              </td></tr>
            )}
            {nodes.map(n => {
              const m = metrics[n.id]
              return (
                <tr key={n.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                  <td className="px-5 py-3">
                    <Link to={`/nodes/${n.id}`} className="font-medium text-neutral-900 hover:underline">{n.name}</Link>
                  </td>
                  <td className="px-4 py-3 text-neutral-600 hidden sm:table-cell">{n.hostname}</td>
                  <td className="px-4 py-3 w-[280px]">
                    {m ? (
                      <div className="space-y-1">
                        <UsageBar label="CPU" percent={m.cpu_percent} />
                        <UsageBar label="RAM" used={m.mem_used} total={m.mem_total} />
                        <UsageBar label="Диск" used={m.disk_used} total={m.disk_total} />
                      </div>
                    ) : <span className="text-[12px] text-neutral-400">{n.cpu_count ? `${n.cpu_count} vCPU · ${humanBytes(n.mem_bytes)}` : '—'}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      <IconCircleDot tone={n.status === 'online' ? 'green' : 'gray'} />
                      <span className="text-[13px]">{STATUS_RU[n.status] || n.status}</span>
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[12px] text-neutral-500">{timeAgo(n.last_heartbeat_at)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>
    </>
  )
}
